import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';

export default function EmailNotificationsManager() {
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    contextTenant,
    contextUser,
    'instructure'
  );
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSendDigests = async () => {
    if (!contextUser) return;
    setIsSending(true);
    setResult(null);

    try {
      const userId = contextUser.user_id || contextUser.id || contextUser.email;

      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'https://api.neogtm.com'
        : 'https://api.neogtm.com';

      const response = await fetch(`${API_BASE_URL}/api/gmail/send-digest`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: userId
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          message: data.message,
          emailsSent: data.emailsSent,
          usersSkipped: data.usersSkipped
        });
      } else {
        setResult({
          success: false,
          message: data.error || 'Failed to send digests'
        });
      }
    } catch (error) {
      console.error('Error sending digests:', error);
      setResult({
        success: false,
        message: error.message || 'Network error'
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleGenerateAlerts = async () => {
    if (!contextUser) return;
    setIsSending(true);
    setResult(null);

    try {
      const userId = contextUser.user_id || contextUser.id || contextUser.email;

      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'https://api.neogtm.com'
        : 'https://api.neogtm.com';

      const response = await fetch(`${API_BASE_URL}/api/gmail/alerts/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: userId
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          message: data.message,
          details: data.details
        });
      } else {
        setResult({
          success: false,
          message: data.error || 'Failed to generate alerts'
        });
      }
    } catch (error) {
      console.error('Error generating alerts:', error);
      setResult({
        success: false,
        message: error.message || 'Network error'
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!contextUser) return;
    setIsSending(true);
    setResult(null);

    try {
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'https://api.neogtm.com'
        : 'https://api.neogtm.com';

      const response = await fetch(`${API_BASE_URL}/api/gmail/test-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: contextUser.id
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResult({
          success: true,
          message: data.message || 'Test email sent successfully!'
        });
      } else {
        setResult({
          success: false,
          message: data.error || 'Failed to send test email. Make sure Gmail is connected in Settings.'
        });
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      setResult({
        success: false,
        message: error.message || 'Network error'
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-[#00C2C2]" />
          <div>
            <CardTitle>Email Notifications Manager</CardTitle>
            <CardDescription>
              Test and manage email alert digests
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-3">
          <Button
            onClick={handleSendTestEmail}
            disabled={isSending}
            variant="outline"
            className="justify-start"
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Send Test Email (to yourself)
              </>
            )}
          </Button>

          <Button
            onClick={handleGenerateAlerts}
            disabled={isSending}
            variant="outline"
            className="justify-start"
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2" />
                Generating...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Generate Alerts (for your account)
              </>
            )}
          </Button>

          <Button
            onClick={handleSendDigests}
            disabled={isSending}
            className="bg-[#00C2C2] hover:bg-[#00A3A3] justify-start"
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Send My Digest (real alerts)
              </>
            )}
          </Button>
        </div>

        {result && (
          <Alert variant={result.success ? "default" : "destructive"}>
            {result.success ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>
              <div className="font-medium">{result.message}</div>
              {result.emailsSent !== undefined && (
                <div className="text-sm mt-1">
                  Emails sent: {result.emailsSent} • Users skipped: {result.usersSkipped}
                </div>
              )}
              {result.details && (
                <div className="text-sm mt-1">
                  Live deals: {result.details.live_deals} • Momentum: {result.details.momentum} • Persona: {result.details.persona}
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="bg-gray-50 p-4 rounded-lg border">
          <h4 className="font-medium text-sm text-[#0A1A2F] mb-2">Backend Setup Required</h4>
          <p className="text-sm text-[#5C6B7A] mb-3">
            Add this endpoint to your Node.js backend (api.neogtm.com):
          </p>
          <div className="space-y-2 text-xs text-[#5C6B7A]">
            <p className="font-medium">File: gmailRoutes.ts</p>
            <pre className="bg-white p-2 rounded text-xs overflow-x-auto">
{`router.post('/api/gmail/test-email', async (req, res) => {
  // See implementation in project docs
  // Uses gmail_tokens table + Google API
  // Sends HTML email with sample alerts
});`}
            </pre>
            <p className="mt-2 font-medium">For scheduled digests:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>GitHub Actions (recommended) - Free cron scheduler</li>
              <li>Vercel Cron - If hosted on Vercel</li>
              <li>Call endpoint daily at 8 AM user timezone</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}